import React, { useState, useMemo, useEffect } from "react";
import { CampLocation } from "@/types/campLocation";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Plus, Copy, ArrowUpDown, Globe } from "lucide-react";
import { useOfferingOptions } from "../hooks/useOfferingOptions";
import { AgeGroupVariationActions } from "./AgeGroupVariationActions";
import { FieldOwnershipBadge } from "@/components/common/FieldOwnershipBadge";
import { encodeFieldPath } from "@/services/campDataFieldState";
import { toast } from "sonner";
import { AgeGroupVariationModal, VariationFormData } from "./AgeGroupVariationModal";
import { useDecimalEnums } from "../hooks/useDecimalEnums";
import { Variation } from "../types/variation";
import { DeleteConfirmationDialog } from "../components/calendar/dialogs/components/DeleteConfirmationDialog";
import { updateCampLocation } from "@/services/campLocationUpdateService";
import { useAuth } from "@/context/AuthContext";
import { db } from "@/services/firebaseService";
import { UserInvitation } from "@/types/userInvitation";
import { useCopyVariations } from "../hooks/useCopyVariations";
import { CopyVariationsModal } from "../components/CopyVariationsModal";
import naturalCompare from "string-natural-compare";
import { SessionDatesFetchModal } from "../components/SessionDatesFetchModal";
import { fetchEnhancedSessionDatesFromURL } from "@/services/aiDataFetchService";
import { DateRange } from "@/services/aiDataFetchService";
import { createSessionsFromDateRanges } from "@/services/sessionFetchService";
import { HtmlSanitizeModal } from "../components/HtmlSanitizeModal";
import { sanitizeHtmlFromURL, HtmlSanitizeMetrics } from "@/services/htmlSanitizeService";
import { HtmlWrapperPatternsModal } from "../components/HtmlWrapperPatternsModal";
import { inferHtmlWrapper, saveWrapperPatternsToFirestore } from "@/services/htmlWrapperInferenceService";
import { SharedWrapperPatternService } from "@/services/sharedWrapperPatternService";
import type { HtmlWrapperPattern, HtmlWrapperInferenceMetrics } from "@/services/htmlWrapperInferenceService";
import { Timestamp } from "firebase/firestore";
import { useWrapperPatterns } from "@/hooks/useWrapperPatterns";

interface AgeGroupsOfferingsSectionProps {
  location: CampLocation;
  isWelcomeScreen?: boolean;
  invitation?: UserInvitation;
  onProgramSaved?: (locationId: string) => Promise<void>;
  showCopyFromLocationButton?: boolean;
  /** Proposal pre-fill from the Automation Admin tab's [Review]; opens the Add-program modal pre-filled. */
  prefillVariation?: Partial<VariationFormData> | null;
  onPrefillConsumed?: () => void;
}

export const AgeGroupsOfferingsSection: React.FC<AgeGroupsOfferingsSectionProps> = ({
  location,
  isWelcomeScreen = false,
  invitation,
  onProgramSaved,
  showCopyFromLocationButton = false,
  prefillVariation = null,
  onPrefillConsumed
}) => {
  const [variationModalOpen, setVariationModalOpen] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState<VariationFormData | null>(null);
  const [isCopyMode, setIsCopyMode] = useState(false);
  const [actionMenuIdx, setActionMenuIdx] = useState<number | null>(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isOpen: boolean;
    index: number | null;
  }>({
    isOpen: false,
    index: null
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [localVariations, setLocalVariations] = useState<Variation[]>([]);
  const [isSorting, setIsSorting] = useState(false);
  const [savedFormDataForAddAnother, setSavedFormDataForAddAnother] = useState<any | null>(null);
  const [savedAddAnotherChecked, setSavedAddAnotherChecked] = useState<boolean>(false);
  const programAddAnotherClearTimerRef = React.useRef<number | null>(null);
  // Test-only marker to assert reopen behavior without relying on portals
  const [testProgramAddAnotherMarker, setTestProgramAddAnotherMarker] = useState<boolean>(false);
  
  // Session fetch modal state
  const [sessionFetchModal, setSessionFetchModal] = useState<{
    isOpen: boolean;
    isFetching: boolean;
    variation: Variation | null;
    dateRanges: DateRange[];
    fetchError: string | null;
    debugInfo: null | {
      processedHtmlContent: string;
      originalBodyContent: string;
      savedPatterns: string[];
      processingMethod: string;
      explanation: string;
      systemPrompt?: string;
      aiResponse?: string;
      systemPromptTemplate?: string;
    };
    urlResults?: Array<{
      url: string;
      success: boolean;
      rangesFound: number;
      error?: string;
    }>;
  }>({
    isOpen: false,
    isFetching: false,
    variation: null,
    dateRanges: [],
    fetchError: null,
    debugInfo: null
  });

  // HTML sanitize modal state
  const [htmlSanitizeModal, setHtmlSanitizeModal] = useState<{
    isOpen: boolean;
    isFetching: boolean;
    variation: Variation | null;
    sanitizedHtml: string;
    rawHtmlContent: string;
    fetchError: string | null;
    metrics?: HtmlSanitizeMetrics;
  }>({
    isOpen: false,
    isFetching: false,
    variation: null,
    sanitizedHtml: '',
    rawHtmlContent: '',
    fetchError: null
  });

  // HTML wrapper patterns modal state
  const [wrapperPatternsModal, setWrapperPatternsModal] = useState<{
    isOpen: boolean;
    isFetching: boolean;
    patterns: HtmlWrapperPattern[];
    fetchError: string | null;
    metrics?: HtmlWrapperInferenceMetrics;
  }>({
    isOpen: false,
    isFetching: false,
    patterns: [],
    fetchError: null
  });

  const [isAnalyzingPatterns, setIsAnalyzingPatterns] = useState(false);

  const { currentUser, userProfile, isAdmin } = useAuth();
  const {
    offeringOptions,
    loading
  } = useOfferingOptions();
  const {
    getShortLabel,
    enumsMap,
    isLoading: isLoadingEnums
  } = useDecimalEnums();

  // Copy variations hook
  const {
    isModalOpen: isCopyVariationsModalOpen,
    isLoading: isCopyVariationsLoading,
    locationOptions,
    showCopyVariationsModal,
    closeCopyVariationsModal,
    executeCopyVariations
  } = useCopyVariations({ location });

  const variations = useMemo(
    () => Array.isArray(localVariations) && localVariations.length > 0
      ? localVariations
      : Array.isArray(location.variations)
        ? location.variations
        : [],
    [localVariations, location.variations]
  );
  // Only show programs whose displayTitle does not start with "__" for non-admins
  const visibleVariations = useMemo(
    () => isAdmin ? variations : variations.filter(v => !(v.displayTitle || "").trim().startsWith("__")),
    [variations, isAdmin]
  );
  const showEmptyRow = visibleVariations.length === 0;
  const getOfferingLabels = (keys?: string[]) => {
    if (!keys || !offeringOptions) return "—";
    const labels = keys.map(key => {
      const match = offeringOptions.find(opt => opt.value === key);
      return match ? match.label : null;
    }).filter(Boolean);
    return labels.length > 0 ? labels.join(", ") : "—";
  };
  const getIncrementLabel = (key?: string) => {
    if (!key || !enumsMap) return null;
    const item = enumsMap.get(key);
    return item ? item.longLabel || item.shortLabel : null;
  };
  const minMaxCell = (v: Variation, isMin: boolean) => {
    if (isMin ? !v.showMinAgeGradeAsPrimary : !v.showMaxAgeGradeAsPrimary) {
      const value = v[isMin ? "minAgeInYears" : "maxAgeInYears"];
      return value != null ? value : "—";
    } else {
      const key = v[isMin ? "minAgeGradeKey" : "maxAgeGradeKey"];
      return getShortLabel(key);
    }
  };
  const handleActionSelect = (action: "edit" | "delete" | "copy" | "copyJson" | "fetchDates" | "sanitizeHtml", idx: number) => {
    const target = visibleVariations[idx];
    if (!target) {
      return;
    }
    if (action === "edit") {
      const variationToEdit = target;
      console.log("[AgeGroupsOfferingsSection] Editing variation:", variationToEdit.key);
      console.log("[AgeGroupsOfferingsSection] FULL VARIATION DATA FOR EDIT:", variationToEdit);
      console.log("[AgeGroupsOfferingsSection] Additional fields in variation:");
      console.log("  - description:", variationToEdit.description);
      console.log("  - url:", variationToEdit.url);
      console.log("  - dataFetchUrl:", variationToEdit.dataFetchUrl);
      console.log("  - defaultSessionUrl:", variationToEdit.defaultSessionUrl);
      console.log("  - defaultSessionDescription:", variationToEdit.defaultSessionDescription);
      
      // Clear saved form data when editing an existing variation
      setSavedFormDataForAddAnother(null);
      setSelectedVariation(variationToEdit);
      setIsCopyMode(false);
      setVariationModalOpen(true);
    } else if (action === "delete") {
      // Map the visible index back to the original variations index
      const originalIndex = variations.findIndex(v => v.key === target.key);
      setDeleteConfirmModal({
        isOpen: true,
        index: originalIndex >= 0 ? originalIndex : null
      });
    } else if (action === "copy") {
      const variationToCopy = target;
      console.log("[AgeGroupsOfferingsSection] Copying variation:", variationToCopy.key);
      // Create a copy of the variation but without the key
      const { key, ...variationWithoutKey } = variationToCopy;
      
      // Clear saved form data when copying an existing variation
      setSavedFormDataForAddAnother(null);
      setSelectedVariation(variationWithoutKey as VariationFormData);
      setIsCopyMode(true);
      setVariationModalOpen(true);
    } else if (action === "copyJson") {
      try {
        const json = JSON.stringify(target, null, 2);
        if (typeof navigator?.clipboard?.writeText === "function") {
          void navigator.clipboard.writeText(json).then(
            () => toast.success("Program copied to clipboard as JSON"),
            () => toast.error("Failed to copy to clipboard")
          );
        } else {
          toast.error("Clipboard not available");
        }
      } catch {
        toast.error("Failed to copy to clipboard");
      }
    } else if (action === "fetchDates") {
      const variationToFetchDates = target;
      console.log("🎯 [AgeGroupsOfferingsSection] FETCH DATES action selected");
      console.log("📋 Variation details:", {
        key: variationToFetchDates.key,
        displayTitle: variationToFetchDates.displayTitle,
        dataFetchUrl: variationToFetchDates.dataFetchUrl
      });

      if (!variationToFetchDates.dataFetchUrl) {
        console.error("❌ [AgeGroupsOfferingsSection] No dataFetchUrl configured");
        // Show modal with error immediately
        setSessionFetchModal({
          isOpen: true,
          isFetching: false,
          variation: variationToFetchDates,
          dateRanges: [],
          fetchError: "No data fetch URL configured for this program. Please edit the program and add a URL.",
          debugInfo: {
            processedHtmlContent: '',
            originalBodyContent: '',
            savedPatterns: [],
            systemPrompt: '',
            aiResponse: '',
            processingMethod: 'Configuration Error',
            explanation: 'No data fetch URL is configured for this program. Please edit the program and add a URL to fetch session dates from.'
          }
        });
        return;
      }
      
      // Parse URLs to show user what we're about to process
      const urls = variationToFetchDates.dataFetchUrl
        .split(/[\s\n\r\t]+/)
        .map(url => url.trim())
        .filter(url => url.length > 0)
        .filter(url => {
          try {
            new URL(url);
            return true;
          } catch {
            return false;
          }
        });

      console.log(`🔗 [AgeGroupsOfferingsSection] Parsed ${urls.length} URL(s) from dataFetchUrl:`, urls);

      // Start the fetch process with enhanced modal state
      setSessionFetchModal({
        isOpen: true,
        isFetching: true,
        variation: variationToFetchDates,
        dateRanges: [],
        fetchError: null,
        debugInfo: {
          processedHtmlContent: '',
          originalBodyContent: '',
          savedPatterns: [],
          systemPrompt: '',
          aiResponse: '',
          processingMethod: 'Initializing...',
          explanation: `Preparing to process ${urls.length} URL${urls.length !== 1 ? 's' : ''}:\n${urls.map((url, i) => `${i + 1}. ${url}`).join('\n')}`
        },
        urlResults: urls.map((url, index) => ({
          url,
          success: false,
          rangesFound: 0,
          error: 'Pending...'
        }))
      });

      // Use the enhanced AI data fetch service with pattern support
      console.log("🚀 [AgeGroupsOfferingsSection] Starting enhanced session fetch...");
      handleFetchSessionDates(variationToFetchDates);
    } else if (action === "sanitizeHtml") {
      const variationToSanitizeHtml = target;
      console.log("[AgeGroupsOfferingsSection] Sanitizing HTML for variation:", variationToSanitizeHtml.key);
      
      // Start the sanitization process immediately
      setHtmlSanitizeModal({
        isOpen: true,
        isFetching: true,
        variation: variationToSanitizeHtml,
        sanitizedHtml: '',
        rawHtmlContent: '',
        fetchError: null,
        metrics: undefined
      });

      // Sanitize HTML from the URL
      handleSanitizeHtml(variationToSanitizeHtml);
    }
    setActionMenuIdx(null);
  };

  const handleFetchSessionDates = async (variation: Variation) => {
    console.log(`🎯 [AgeGroupsOfferingsSection] STARTING progressive session dates fetch process`);
    console.log(`📋 Variation details:`, {
      key: variation.key,
      displayTitle: variation.displayTitle,
      dataFetchUrl: variation.dataFetchUrl
    });

    try {
      if (!variation.dataFetchUrl) {
        const error = "No data fetch URL configured for this program";
        console.error(`❌ [AgeGroupsOfferingsSection] ${error}`);
        
        setSessionFetchModal(prev => ({
          ...prev,
          isFetching: false,
          dateRanges: [],
          fetchError: error,
          debugInfo: {
            processedHtmlContent: '',
            originalBodyContent: '',
            savedPatterns: [],
            systemPrompt: '',
            aiResponse: '',
            processingMethod: 'Configuration Error',
            explanation: 'No data fetch URL is configured for this program. Please edit the program and add a URL to fetch session dates from.'
          }
        }));
        return;
      }

      console.log(`🚀 [AgeGroupsOfferingsSection] Starting progressive session fetch service...`);
      console.log(`🔗 Target URL(s): ${variation.dataFetchUrl}`);
      console.log(`🏢 Location ID: ${location.id}`);
      
      // Parse URLs to show initial progress
      const urls = variation.dataFetchUrl
        .split(/[\s\n\r\t]+/)
        .map(url => url.trim())
        .filter(url => url.length > 0)
        .filter(url => {
          try {
            new URL(url);
            return true;
          } catch {
            return false;
          }
        });

      console.log(`📊 [AgeGroupsOfferingsSection] Will process ${urls.length} URLs progressively`);

      // Initialize modal with progressive tracking
      setSessionFetchModal({
        isOpen: true,
        isFetching: true,
        variation: variation,
        dateRanges: [],
        fetchError: null,
        debugInfo: {
          processedHtmlContent: '',
          originalBodyContent: '',
          savedPatterns: [],
          systemPrompt: '',
          aiResponse: '',
          processingMethod: `Progressive Processing - 0/${urls.length} URLs completed`,
          explanation: `Processing ${urls.length} URLs individually for real-time progress:\n${urls.map((url, i) => `${i + 1}. ${url} - Pending...`).join('\n')}`
        },
        urlResults: urls.map((url) => ({
          url,
          success: false,
          rangesFound: 0,
          error: 'Pending...'
        }))
      });
      
      const result = await fetchEnhancedSessionDatesFromURL(
        variation.dataFetchUrl,
        variation.displayTitle || variation.key,
        location.id,
        // Progressive callback function
        (progress) => {
          console.log(`📈 [AgeGroupsOfferingsSection] Progress Update: URL ${progress.currentUrl}/${progress.totalUrls} - ${progress.status}`);
          console.log(`🔗 Current URL: ${progress.url.substring(0, 100)}${progress.url.length > 100 ? '...' : ''}`);
          
          if (progress.result) {
            console.log(`📊 Result: Success=${progress.result.success}, Sessions=${progress.result.dateRanges.length}`);
          }

          // Update modal state with current progress
          setSessionFetchModal(prev => {
            // Update URL results with current progress
            const updatedUrlResults = [...(prev.urlResults || [])];
            const urlIndex = progress.currentUrl - 1;
            
            if (urlIndex >= 0 && urlIndex < updatedUrlResults.length) {
              updatedUrlResults[urlIndex] = {
                url: progress.url,
                success: progress.status === 'completed' && !!progress.result?.success,
                rangesFound: progress.result?.dateRanges.length || 0,
                error: progress.status === 'failed' ? progress.result?.error : 
                       progress.status === 'processing' ? 'Processing...' : 
                       progress.status === 'completed' && !progress.result?.success ? progress.result?.error || 'No sessions found' : 
                       undefined
              };
            }

            // Accumulate all successful date ranges so far
            const allDateRanges: DateRange[] = [];
            updatedUrlResults.forEach((urlResult, index) => {
              if (urlResult.success && progress.currentUrl > index + 1) {
                // This URL was already processed successfully - we need the actual ranges
                // For now, we'll update this when the final result comes in
              }
            });

            // Add current URL's results if completed
            if (progress.status === 'completed' && progress.result?.success && progress.result.dateRanges) {
              allDateRanges.push(...progress.result.dateRanges);
            }

            const completedUrls = updatedUrlResults.filter(r => r.error !== 'Processing...' && r.error !== 'Pending...').length;
            
            return {
              ...prev,
              dateRanges: prev.dateRanges.concat(progress.result?.dateRanges || []), // Accumulate ranges
              urlResults: updatedUrlResults,
              debugInfo: {
                ...prev.debugInfo!,
                processingMethod: `Progressive Processing - ${completedUrls}/${progress.totalUrls} URLs completed`,
                explanation: `Processing ${progress.totalUrls} URLs individually:\n${updatedUrlResults.map((result, i) => 
                  `${i + 1}. ${result.url.substring(0, 60)}${result.url.length > 60 ? '...' : ''} - ${
                    result.error === 'Processing...' ? '🔄 Processing...' :
                    result.error === 'Pending...' ? '⏳ Pending...' :
                    result.success ? `✅ ${result.rangesFound} sessions` :
                    `❌ ${result.error}`
                  }`
                ).join('\n')}`
              }
            };
          });
        }
      );

      console.log(`📦 [AgeGroupsOfferingsSection] Progressive processing completed. Final result:`, {
        success: result.success,
        dateRangesCount: result.dateRanges?.length || 0,
        hasError: !!result.error,
        hasDebugInfo: !!result.debugInfo,
        hasUrlResults: !!result.urlResults,
        error: result.error
      });

      // Final update with complete results
      setSessionFetchModal(prev => ({
        ...prev,
        isFetching: false,
        dateRanges: result.dateRanges || [],
        fetchError: result.success ? null : (result.error || "No session dates found"),
        debugInfo: result.debugInfo || prev.debugInfo,
        urlResults: result.urlResults || prev.urlResults
      }));

      if (result.success && result.dateRanges && result.dateRanges.length > 0) {
        console.log(`✅ [AgeGroupsOfferingsSection] SUCCESS - Progressive fetch completed successfully`);
        console.log(`📊 Final Summary:`, {
          totalDateRanges: result.dateRanges.length,
          successfulUrls: result.urlResults?.filter(r => r.success).length || 0,
          failedUrls: result.urlResults?.filter(r => !r.success).length || 0,
          patternsApplied: result.processingMetrics?.patternsApplied || 0,
          contentReduction: result.processingMetrics ? 
            `${result.processingMetrics.contentReductionPercent.toFixed(1)}%` : 'N/A',
          processingTime: result.processingMetrics ? 
            `${result.processingMetrics.timeElapsedMs}ms` : 'N/A'
        });
      } else {
        console.warn(`⚠️ [AgeGroupsOfferingsSection] Progressive processing completed but no sessions found`);
      }

    } catch (error) {
      console.error(`❌ [AgeGroupsOfferingsSection] CRITICAL ERROR during progressive session fetch:`, error);
      
      // Log detailed error information
      if (error instanceof Error) {
        console.error(`   Error name: ${error.name}`);
        console.error(`   Error message: ${error.message}`);
        console.error(`   Error stack:`, error.stack);
      } else {
        console.error(`   Error object:`, error);
      }

      setSessionFetchModal(prev => ({
        ...prev,
        isFetching: false,
        dateRanges: [],
        fetchError: error instanceof Error ? error.message : "Failed to fetch session dates",
        debugInfo: {
          processedHtmlContent: '',
          originalBodyContent: '',
          savedPatterns: [],
          systemPrompt: '',
          aiResponse: '',
          processingMethod: 'Client Error',
          explanation: `Error occurred in progressive client processing: ${error instanceof Error ? error.message : String(error)}`
        }
      }));
    }
  };

  const handleCreateSessionsFromDates = async (selectedRanges: DateRange[]) => {
    if (!sessionFetchModal.variation || !currentUser) {
      toast.error("Missing required data to create sessions");
      return;
    }

    try {
      console.log(`[AgeGroupsOfferingsSection] Creating ${selectedRanges.length} sessions`);
      
      const result = await createSessionsFromDateRanges(
        db,
        location.id,
        sessionFetchModal.variation,
        selectedRanges,
        currentUser.uid
      );

      if (result.success) {
        // Close the modal
        setSessionFetchModal({
          isOpen: false,
          isFetching: false,
          variation: null,
          dateRanges: [],
          fetchError: null,
          debugInfo: null
        });

        // Success message is already handled by the service
        console.log(`[AgeGroupsOfferingsSection] Successfully created ${result.createdCount} sessions`);
      } else {
        toast.error("Failed to create sessions");
      }
    } catch (error) {
      console.error("[AgeGroupsOfferingsSection] Error creating sessions:", error);
      toast.error("Failed to create sessions");
    }
  };

  const handleAddVariation = () => {
    console.log("[AgeGroupsOfferingsSection] Opening modal for adding new variation");
    // Clear any saved form data when opening for normal add (not "Add another")
    setSavedFormDataForAddAnother(null);
    setSavedAddAnotherChecked(false);
    setSelectedVariation(null);
    setIsCopyMode(false);
    setVariationModalOpen(true);
  };
  const handleVariationSaved = async (updatedLocation: CampLocation) => {
    console.log("[AgeGroupsOfferingsSection] Variation saved, location updated:", updatedLocation.variations ? updatedLocation.variations.length : 0, "variations");
    if (updatedLocation.variations) {
      setLocalVariations(updatedLocation.variations);
    }
    setVariationModalOpen(false);
    setSelectedVariation(null);

    // If this is the welcome screen and we have a callback, trigger auto-publish
    if (isWelcomeScreen && onProgramSaved) {
      console.log("[AgeGroupsOfferingsSection] Triggering auto-publish after program save in welcome screen");
      try {
        await onProgramSaved(location.id);
        console.log("[AgeGroupsOfferingsSection] Auto-publish completed successfully");
      } catch (error) {
        console.error("[AgeGroupsOfferingsSection] Error during auto-publish:", error);
        // Don't show an error toast here as the publish function will handle that
      }
    }
  };
  const handleDeleteVariation = async () => {
    if (deleteConfirmModal.index === null || !currentUser) {
      setDeleteConfirmModal({
        isOpen: false,
        index: null
      });
      return;
    }

    try {
      setIsDeleting(true);
      console.log("[AgeGroupsOfferingsSection] Attempting to delete variation at index:", deleteConfirmModal.index);

      // Create a new array without the deleted variation
      const updatedVariations = [...variations];
      const deletedVariation = updatedVariations.splice(deleteConfirmModal.index, 1)[0];
      console.log(`[AgeGroupsOfferingsSection] Removed variation with key ${deletedVariation.key}`);

      // Update the database with the new variations array
      await updateCampLocation(db, location.id, {
        variations: updatedVariations
      }, currentUser.uid, `Deleted program: ${deletedVariation.displayTitle || deletedVariation.key}`, true);

      // Update local state
      setLocalVariations(updatedVariations);

      toast.success("Program deleted successfully");
      console.log("[AgeGroupsOfferingsSection] Variation deleted successfully");
    } catch (error) {
      console.error("[AgeGroupsOfferingsSection] Error deleting variation:", error);
      toast.error("Failed to delete program");
    } finally {
      setIsDeleting(false);
      setDeleteConfirmModal({
        isOpen: false,
        index: null
      });
    }
  };
  const handleMigrationComplete = (updatedLocation: CampLocation) => {
    console.log("[AgeGroupsOfferingsSection] Migration completed, updating variations");
    if (updatedLocation.variations) {
      setLocalVariations(updatedLocation.variations);
    }
  };

  const handleSortVariationsAZ = async () => {
    if (!currentUser || variations.length === 0) {
      return;
    }

    try {
      setIsSorting(true);
      console.log("[AgeGroupsOfferingsSection] Sorting variations A-Z alphanumerically by displayTitle");

      // Sort variations alphanumerically by displayTitle using natural compare
      const sortedVariations = [...variations].sort((a, b) => {
        const titleA = a.displayTitle || "";
        const titleB = b.displayTitle || "";
        return naturalCompare(titleA, titleB, { caseInsensitive: true });
      });

      console.log("[AgeGroupsOfferingsSection] Original order:", variations.map(v => v.displayTitle));
      console.log("[AgeGroupsOfferingsSection] Sorted order:", sortedVariations.map(v => v.displayTitle));

      // Update the database with the sorted variations array
      await updateCampLocation(db, location.id, {
        variations: sortedVariations
      }, currentUser.uid, "Sorted programs A-Z by name", true);

      // Update local state
      setLocalVariations(sortedVariations);

      toast.success("Programs sorted A-Z successfully");
      console.log("[AgeGroupsOfferingsSection] Variations sorted successfully");
    } catch (error) {
      console.error("[AgeGroupsOfferingsSection] Error sorting variations:", error);
      toast.error("Failed to sort programs");
    } finally {
      setIsSorting(false);
    }
  };

  // Function to get price from either regularPrice or priceRegular field
  const getPriceDisplay = (v: Variation) => {
    const price = v.regularPrice !== undefined && v.regularPrice !== null ? v.regularPrice : v.priceRegular !== undefined && v.priceRegular !== null ? v.priceRegular : null;
    return price !== null ? `$${price.toFixed(2)}` : "—";
  };

  // Enhanced function to display regular pricing AND alternative pricing options
  const getPricingDisplayWithAlternatives = (v: Variation) => {
    const regularPrice = v.regularPrice !== undefined && v.regularPrice !== null ? v.regularPrice : v.priceRegular !== undefined && v.priceRegular !== null ? v.priceRegular : null;
    const regularPriceDisplay = regularPrice !== null ? `$${regularPrice.toFixed(2)}` : "—";
    
    // Get the regular length label
    const regularLengthLabel = getIncrementLabel(v.regularOfferIncrementKey);
    
    return (
      <div className="flex flex-col space-y-1">
        {/* Regular pricing */}
        <div className="flex flex-col">
          {v.regularOfferIncrementKey && (
            <span className="text-sm text-muted-foreground">
              {regularLengthLabel || "—"}
            </span>
          )}
          <span className="font-medium">{regularPriceDisplay}</span>
        </div>
        
        {/* Alternative pricing options */}
        {v.regularOfferIncrementAlternatives && v.regularOfferIncrementAlternatives.length > 0 && (
          <div className="border-t pt-1 mt-1">
            {v.regularOfferIncrementAlternatives.map((alt, altIndex) => {
              const altLengthLabel = getIncrementLabel(alt.key);
              const altPriceDisplay = alt.price !== null ? `$${alt.price.toFixed(2)}` : "—";
              
              return (
                <div key={altIndex} className="flex flex-col mb-1 last:mb-0">
                  <span className="text-xs text-muted-foreground">
                    {altLengthLabel || alt.key}
                  </span>
                  <span className="text-xs text-slate-600">{altPriceDisplay}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const handleSanitizeHtml = async (variation: Variation) => {
    try {
      if (!variation.dataFetchUrl) {
        throw new Error("No data fetch URL configured for this program");
      }

      console.log(`[AgeGroupsOfferingsSection] Starting HTML sanitization for variation: ${variation.key}`);
      console.log(`[AgeGroupsOfferingsSection] Original dataFetchUrl: ${variation.dataFetchUrl}`);
      
      // Parse URLs using the same logic as handleFetchSessionDates and handleFindWrapperPatterns
      const parsedUrls = variation.dataFetchUrl
        .split(/[\s\n\r\t,]+/)  // Split by whitespace, newlines, tabs, and commas
        .map(url => url.trim())
        .filter(url => url.length > 0)
        .filter(url => {
          try {
            new URL(url);
            return true;
          } catch {
            console.warn(`[AgeGroupsOfferingsSection] Invalid URL found in dataFetchUrl: ${url}`);
            return false;
          }
        });

      console.log(`[AgeGroupsOfferingsSection] Parsed ${parsedUrls.length} valid URLs:`, parsedUrls);
      
      if (parsedUrls.length === 0) {
        throw new Error("No valid URLs found in data fetch URL field");
      }

      // For now, use the first valid URL for HTML sanitization
      // TODO: Consider processing all URLs and combining results
      const urlToProcess = parsedUrls[0];
      
      if (parsedUrls.length > 1) {
        console.log(`[AgeGroupsOfferingsSection] Multiple URLs detected. Processing first URL: ${urlToProcess}`);
        console.log(`[AgeGroupsOfferingsSection] Remaining URLs (${parsedUrls.length - 1}): ${parsedUrls.slice(1).join(', ')}`);
      }
      
      const result = await sanitizeHtmlFromURL(
        urlToProcess,
        variation.displayTitle || variation.key,
        location.id  // Pass the locationId for pattern application
      );

      if (result.success) {
        setHtmlSanitizeModal(prev => ({
          ...prev,
          isFetching: false,
          sanitizedHtml: result.sanitizedHtml,
          rawHtmlContent: result.rawHtmlContent,
          fetchError: null,
          metrics: result.metrics
        }));
        
        console.log(`[AgeGroupsOfferingsSection] HTML sanitization successful for URL: ${urlToProcess}`);
      } else {
        setHtmlSanitizeModal(prev => ({
          ...prev,
          isFetching: false,
          sanitizedHtml: '',
          rawHtmlContent: result.rawHtmlContent,
          fetchError: result.error || "Failed to sanitize HTML content"
        }));
        
        console.error(`[AgeGroupsOfferingsSection] HTML sanitization failed for URL: ${urlToProcess}`, result.error);
      }
    } catch (error) {
      console.error("[AgeGroupsOfferingsSection] Error sanitizing HTML:", error);
      setHtmlSanitizeModal(prev => ({
        ...prev,
        isFetching: false,
        sanitizedHtml: '',
        rawHtmlContent: '',
        fetchError: error instanceof Error ? error.message : "Failed to sanitize HTML"
      }));
    }
  };

  // Initialize wrapper patterns hook
  const {
    patterns,
    savedPatterns,
    selectedPatterns,
    isLoading: isPatternsLoading,
    findPatterns: findWrapperPatternsFromHook,
    saveSelectedPatterns,
    loadSavedPatterns,
    togglePatternSelection,
    selectAllPatterns,
    clearSelection
  } = useWrapperPatterns({
    context: {
      type: 'programs',
      locationId: location?.id || '',
      organizationId: typeof location?.providedByOrg === 'object' ? location.providedByOrg?.id : location?.providedByOrg
    },
    urls: variations
      .map(v => v.dataFetchUrl)
      .filter(Boolean)
      .flatMap(url => url.split(/[\s\n\r\t]+/))
      .map(url => url.trim())
      .filter(url => {
        try {
          new URL(url);
          return true;
        } catch {
          return false;
        }
      }),
    autoLoad: true
  });

  // Replace handleFindWrapperPatterns with hook-based version
  const handleFindWrapperPatterns = async () => {
    console.log("[AgeGroupsOfferingsSection] Finding wrapper patterns for location:", location.id);
    
    // Get all valid URLs from variations
    const validUrls = variations
      .map(v => v.dataFetchUrl)
      .filter(Boolean)
      .flatMap(url => url.split(/[\s\n\r\t]+/))
      .map(url => url.trim())
      .filter(url => {
        try {
          new URL(url);
          return true;
        } catch {
          return false;
        }
      });

    if (validUrls.length < 2) {
      toast.error("At least 2 valid URLs are required for pattern inference");
      return;
    }

    // Set loading state
    setIsAnalyzingPatterns(true);
    setWrapperPatternsModal({
      isOpen: true,
      isFetching: true,
      patterns: [],
      fetchError: null,
      metrics: undefined
    });

    try {
      const result = await findWrapperPatternsFromHook();
      
      if (!result) {
        throw new Error("Pattern inference failed - no result returned");
      }
      
      // Get the patterns from the inference result
      const patternsWithUrls: HtmlWrapperPattern[] = result.patterns.map(p => ({
        pattern: p.pattern,
        description: p.description || `Pattern ${result.patterns.indexOf(p) + 1}`,
        confidence: p.confidence || 0,
        avgContentScore: p.avgContentScore || 0,
        urlsMatched: validUrls // Pass all valid URLs
      }));

      setWrapperPatternsModal(prev => ({
        ...prev,
        isFetching: false,
        patterns: patternsWithUrls,
        fetchError: null,
        metrics: result.metrics
      }));
    } catch (error) {
      console.error("[AgeGroupsOfferingsSection] Error analyzing wrapper patterns:", error);
      setWrapperPatternsModal(prev => ({
        ...prev,
        isFetching: false,
        patterns: [],
        fetchError: error instanceof Error ? error.message : "Failed to analyze wrapper patterns",
        metrics: undefined
      }));
    } finally {
      setIsAnalyzingPatterns(false);
    }
  };

  // Add pre-select logic for Programs tab
  React.useEffect(() => {
    if (wrapperPatternsModal.isOpen && patterns.length > 0 && savedPatterns.length > 0) {
      const savedPatternStrings = new Set(savedPatterns.map(p => p.pattern));
      const indicesToSelect = patterns
        .map((p, idx) => savedPatternStrings.has(p.pattern) ? idx : null)
        .filter(idx => idx !== null) as number[];
      if (indicesToSelect.length > 0) {
        clearSelection();
        indicesToSelect.forEach(idx => togglePatternSelection(idx));
      }
    }
  }, [wrapperPatternsModal.isOpen, patterns, savedPatterns, clearSelection, togglePatternSelection]);

  // Replace handleSaveWrapperPatterns with hook-based version
  const handleSaveWrapperPatterns = async () => {
    try {
      await saveSelectedPatterns();
      toast.success(`Successfully saved wrapper patterns to database`);
      setWrapperPatternsModal(prev => ({ ...prev, isOpen: false })); // Close modal after save
    } catch (error) {
      console.error("[AgeGroupsOfferingsSection] Error saving wrapper patterns:", error);
      toast.error("Failed to save patterns to database");
    }
  };

  // For debugging
  React.useEffect(() => {
    if (variations.length > 0) {
      console.log("[PROGRAMS] Rendering", variations.length, "programs");
      variations.forEach((v, i) => {
        console.log(`[PROGRAMS] Program ${i + 1}:`, {
          key: v.key,
          displayTitle: v.displayTitle,
          min: minMaxCell(v, true),
          max: minMaxCell(v, false),
          offeringTypeKeys: v.offeringTypeKeys
        });
      });
    }
  }, [variations]);

  // Listen for "Add another" events from program modal to reopen with saved data
  React.useEffect(() => {
    const handleProgramAddAnother = (event: CustomEvent) => {
      console.log("[AgeGroupsOfferingsSection] Received programAddAnother event, reopening program modal");
      // Cancel any pending clear
      if (programAddAnotherClearTimerRef.current) {
        clearTimeout(programAddAnotherClearTimerRef.current);
        programAddAnotherClearTimerRef.current = null;
      }
      if (event.detail?.formData) {
        setSavedFormDataForAddAnother(event.detail.formData);
      }
      if (typeof event.detail?.addAnotherChecked === 'boolean') {
        setSavedAddAnotherChecked(event.detail.addAnotherChecked);
      }
      // Reopen the modal immediately for deterministic tests
      setIsCopyMode(false);
      setSelectedVariation(null);
      setVariationModalOpen(true);
      // Set test marker when running under tests
      if (process.env.NODE_ENV === 'test') {
        console.log('[AgeGroupsOfferingsSection] Setting testProgramAddAnotherMarker=true');
        setTestProgramAddAnotherMarker(true);
        try {
          (window as any).__programAddAnotherReopened = true;
        } catch (error) {
          console.warn('[AgeGroupsOfferingsSection] Unable to set test reopen flag', error);
        }
      }
    };
    window.addEventListener('programAddAnother', handleProgramAddAnother as EventListener);
    return () => window.removeEventListener('programAddAnother', handleProgramAddAnother as EventListener);
  }, []);

  // KB-1373: when the Automation Admin tab's [Review] hands a proposal up to the Programs tab, open
  // the Add-program modal pre-filled. Uses the savedFormDataForAddAnother seam (ADD mode → the
  // duplicate-key guard stays active and a colliding proposal key can never overwrite a real
  // variation, unlike edit-mode pre-fill). Consumed once so it doesn't re-open on re-render.
  useEffect(() => {
    if (!prefillVariation) return;
    setSavedFormDataForAddAnother(prefillVariation);
    setSavedAddAnotherChecked(false);
    setIsCopyMode(false);
    setSelectedVariation(null);
    setVariationModalOpen(true);
    onPrefillConsumed?.();
  }, [prefillVariation, onPrefillConsumed]);

  return <div className="py-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-left">Programs</h2>
          <p className="text-muted-foreground text-sm">Enter the programs at this location. Then add sessions for each program.</p>
        </div>
        <div className="flex gap-2 items-center">
          {(userProfile?.role === 'admin_user' && !isWelcomeScreen) || (isWelcomeScreen && showCopyFromLocationButton) ? (
            <Button
              onClick={showCopyVariationsModal}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              disabled={isCopyVariationsLoading}
            >
              <Copy className="w-4 h-4" />
              <span>Copy from location...</span>
            </Button>
          ) : null}
          <Button onClick={handleAddVariation} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2">
            <Plus size={18} /> 
            <span className="font-medium">Add program</span>
          </Button>
          {userProfile?.role === 'admin_user' && !isWelcomeScreen && (
            <>
              <Button
                onClick={handleSortVariationsAZ}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                disabled={isSorting || variations.length === 0}
              >
                <ArrowUpDown className="w-4 h-4" />
                <span>{isSorting ? "Sorting..." : "Sort A-Z"}</span>
              </Button>
              <Button
                onClick={handleFindWrapperPatterns}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                disabled={variations.length < 2 || isAnalyzingPatterns}
              >
                {isAnalyzingPatterns ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                ) : (
                  <Globe className="w-4 h-4" />
                )}
                <span>{isAnalyzingPatterns ? "Analyzing..." : "Find wrapper patterns"}</span>
              </Button>
            </>
          )}
        </div>
      </div>
      
      {/* Test-only proxy marker to detect modal open state without relying on portal */}
      {process.env.NODE_ENV === 'test' && variationModalOpen && (
        <span data-testid="add-program-open" style={{ display: 'none' }}>open</span>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-left">Program name</TableHead>
            <TableHead className="text-left">Min age (or grade)</TableHead>
            <TableHead className="text-left">Max age (or grade)</TableHead>
            <TableHead className="text-left">Types of offering</TableHead>
            <TableHead className="text-left">Regular length and price</TableHead>
            <TableHead className="text-left"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {showEmptyRow ? <TableRow>
              <TableCell colSpan={6} className="italic text-muted-foreground text-left">No programs yet. Add one above.</TableCell>
            </TableRow> : visibleVariations.map((v, i) => <TableRow key={v.key || i}>
                <TableCell className="text-left">
                  <div className="flex flex-col gap-0.5">
                    <button className="text-blue-600 hover:underline text-left" type="button" onClick={() => {
                      // Clear saved form data when editing an existing variation
                      setSavedFormDataForAddAnother(null);
                      setSelectedVariation(v);
                      setIsCopyMode(false);
                      setVariationModalOpen(true);
                    }}>
                      {v.displayTitle || "—"}
                    </button>
                    {isAdmin && v.key != null && v.key !== "" && (
                      <span style={{ color: "#888" }}>{v.key}</span>
                    )}
                    {v.key != null && v.key !== "" && (
                      <FieldOwnershipBadge fieldPath={encodeFieldPath(["variations", { key: v.key }])} />
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-left">{minMaxCell(v, true)}</TableCell>
                <TableCell className="text-left">{minMaxCell(v, false)}</TableCell>
                <TableCell className="text-left">{getOfferingLabels(v.offeringTypeKeys)}</TableCell>
                <TableCell className="text-left">
                  {getPricingDisplayWithAlternatives(v)}
                </TableCell>
                <TableCell className="text-left">
                  <AgeGroupVariationActions 
                    onAction={action => handleActionSelect(action, i)} 
                    variation={v}
                    isWelcomeScreen={isWelcomeScreen}
                  />
                </TableCell>
              </TableRow>)}
        </TableBody>
      </Table>

      {process.env.NODE_ENV === 'test' && testProgramAddAnotherMarker && (
        <span data-testid="program-add-another-reopened" style={{ display: 'none' }}>open</span>
      )}

      <AgeGroupVariationModal 
        isOpen={variationModalOpen} 
        onClose={() => {
          setVariationModalOpen(false);
          setIsCopyMode(false);
          // Clear saved form data when modal closes normally
          if (programAddAnotherClearTimerRef.current) {
            clearTimeout(programAddAnotherClearTimerRef.current);
          }
          programAddAnotherClearTimerRef.current = window.setTimeout(() => {
            setSavedFormDataForAddAnother(null);
            setSavedAddAnotherChecked(false);
            programAddAnotherClearTimerRef.current = null;
          }, 600);
        }} 
        location={location} 
        variationToEdit={selectedVariation} 
        onSaved={handleVariationSaved} 
        isWelcomeScreen={isWelcomeScreen}
        isCopyMode={isCopyMode}
        savedFormDataForAddAnother={savedFormDataForAddAnother}
      />

      <CopyVariationsModal
        isOpen={isCopyVariationsModalOpen}
        onClose={closeCopyVariationsModal}
        onConfirm={executeCopyVariations}
        locationOptions={locationOptions}
        destinationLocationName={location.name || 'this location'}
        isLoading={isCopyVariationsLoading}
      />

      <DeleteConfirmationDialog
        isOpen={deleteConfirmModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteConfirmModal({ isOpen: false, index: null });
          }
        }}
        onDelete={handleDeleteVariation}
        isDeleting={isDeleting}
        title="Delete program"
        description="Are you sure you want to delete this program?"
        itemName={
          deleteConfirmModal.index !== null && variations[deleteConfirmModal.index]
            ? `${variations[deleteConfirmModal.index].displayTitle || variations[deleteConfirmModal.index].key || 'Unnamed Program'}${
                variations[deleteConfirmModal.index].minAgeInYears !== null && 
                variations[deleteConfirmModal.index].maxAgeInYears !== null
                  ? ` (${variations[deleteConfirmModal.index].minAgeInYears} - ${variations[deleteConfirmModal.index].maxAgeInYears})`
                  : variations[deleteConfirmModal.index].minAgeGradeKey && variations[deleteConfirmModal.index].maxAgeGradeKey
                  ? ` (${getShortLabel(variations[deleteConfirmModal.index].minAgeGradeKey)} - ${getShortLabel(variations[deleteConfirmModal.index].maxAgeGradeKey)})`
                  : ''
              }`
            : undefined
        }
      />

      <SessionDatesFetchModal
        isOpen={sessionFetchModal.isOpen}
        onClose={() => {
          setSessionFetchModal({
            isOpen: false,
            isFetching: false,
            variation: null,
            dateRanges: [],
            fetchError: null,
            debugInfo: null
          });
        }}
        onCreateSessions={handleCreateSessionsFromDates}
        dateRanges={sessionFetchModal.dateRanges}
        programName={sessionFetchModal.variation?.displayTitle || sessionFetchModal.variation?.key || ""}
        dataFetchUrl={sessionFetchModal.variation?.dataFetchUrl || ""}
        isFetching={sessionFetchModal.isFetching}
        fetchError={sessionFetchModal.fetchError}
        debugInfo={sessionFetchModal.debugInfo}
        urlResults={sessionFetchModal.urlResults}
        locationId={location.id}
        programKey={sessionFetchModal.variation?.key || ""}
        onRefetch={() => {
          // Re-run the fetch with the same variation
          if (sessionFetchModal.variation) {
            handleFetchSessionDates(sessionFetchModal.variation);
          }
        }}
      />

      <HtmlSanitizeModal
        isOpen={htmlSanitizeModal.isOpen}
        onClose={() => {
          setHtmlSanitizeModal({
            isOpen: false,
            isFetching: false,
            variation: null,
            sanitizedHtml: '',
            rawHtmlContent: '',
            fetchError: null
          });
        }}
        sanitizedHtml={htmlSanitizeModal.sanitizedHtml}
        rawHtmlContent={htmlSanitizeModal.rawHtmlContent}
        programName={htmlSanitizeModal.variation?.displayTitle || htmlSanitizeModal.variation?.key || ''}
        dataFetchUrl={htmlSanitizeModal.variation?.dataFetchUrl || ''}
        isFetching={htmlSanitizeModal.isFetching}
        fetchError={htmlSanitizeModal.fetchError}
        metrics={htmlSanitizeModal.metrics}
      />

      <HtmlWrapperPatternsModal
        isOpen={wrapperPatternsModal.isOpen}
        onClose={() => {
          setWrapperPatternsModal({
            isOpen: false,
            isFetching: false,
            patterns: [],
            fetchError: null
          });
        }}
        patterns={wrapperPatternsModal.patterns}
        locationId={location.id}
        organizationId={location.organizationId}
        fetchError={wrapperPatternsModal.fetchError}
        metrics={wrapperPatternsModal.metrics}
        selectedPatterns={selectedPatterns}
        togglePatternSelection={togglePatternSelection}
        selectAllPatterns={selectAllPatterns}
        clearSelection={clearSelection}
        onSavePatterns={handleSaveWrapperPatterns}
        isSaving={isPatternsLoading}
        isFetching={wrapperPatternsModal.isFetching}
        context="programs"
      />
    </div>;
};
